import type { ChecklistOverview } from '@acasa/shared'
import { useMemo } from 'react'
import { NO_STATUS } from './use-table-state'

export type FacetOption = { value: string; label: string; hint: string }

/**
 * Builds the filter menus.
 *
 * Counts come from the WHOLE dataset, not the currently filtered rows, so a
 * number next to a status does not move around while you tick other boxes.
 * Estates onOffice left without a status2 get an explicit "(ohne Status)"
 * entry — otherwise they would be silently unreachable.
 */
export function useFacetOptions(data: ChecklistOverview | undefined) {
  return useMemo(() => {
    const build = (
      pick: (estate: ChecklistOverview['estates'][number]) => string | null,
      fallback?: string,
    ) => {
      const counts = new Map<string, number>()

      for (const estate of data?.estates ?? []) {
        const value = pick(estate) ?? fallback
        if (value === undefined) continue
        counts.set(value, (counts.get(value) ?? 0) + 1)
      }

      return [...counts.entries()]
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'de'))
        .map(([value, count]): FacetOption => ({ value, label: value, hint: String(count) }))
    }

    return {
      status: build((estate) => estate.status, NO_STATUS),
      agent: build((estate) => estate.agent),
      type: build((estate) => estate.type),
      city: build((estate) => estate.city),
      marketingType: build((estate) => estate.marketingType),
      activeState: build((estate) => estate.activeState),
    }
  }, [data])
}
